import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import axios from 'axios'
import './ParentDashboard.css'

export default function ParentDashboard() {
  const [invites, setInvites] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [parentName, setParentName] = useState('')

  useEffect(() => {
    loadInvites()
  }, [])

  const loadInvites = async () => {
    try {
      const response = await axios.get('/api/parent/invites')
      setInvites(response.data)
      setError(null)
    } catch (error) {
      // COPPA compliance: Handle 403 (child account accessing parent dashboard)
      if (error.response?.status === 403) {
        setError({
          type: 'access_denied',
          message: error.response.data.message || 'This area requires parent permission.'
        })
      } else {
        console.error('Failed to load invites:', error)
        setError({
          type: 'generic',
          message: 'Failed to load invites. Please try again.'
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (inviteId) => {
    if (!parentName) {
      alert('Please enter your name first')
      return
    }

    try {
      await axios.post(`/api/parent/invites/${inviteId}/approve`, {
        parent_name: parentName
      })
      await loadInvites()
      alert('Invite approved!')
    } catch (error) {
      console.error('Failed to approve invite:', error)
      alert('Failed to approve invite')
    }
  }

  const handleReject = async (inviteId) => {
    if (!confirm('Are you sure you want to reject this invite?')) {
      return
    }

    try {
      await axios.post(`/api/parent/invites/${inviteId}/reject`)
      await loadInvites()
      alert('Invite rejected')
    } catch (error) {
      console.error('Failed to reject invite:', error)
      alert('Failed to reject invite')
    }
  }

  return (
    <motion.div
      className="parent-dashboard"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      <div className="container">
        <div className="parent-header">
          <h1 className="page-title">👨‍👩‍👦 Parent Dashboard</h1>
          <p className="parent-subtitle">
            Approve or deny friend requests for Ocean Daniel
          </p>
        </div>

        <div className="parent-name-input">
          <label htmlFor="parent-name">Your Name:</label>
          <input
            id="parent-name"
            type="text"
            className="input"
            placeholder="Enter your name"
            value={parentName}
            onChange={(e) => setParentName(e.target.value)}
          />
        </div>

        {error && error.type === 'access_denied' ? (
          <div className="parent-access-denied">
            <div className="access-denied-icon">🔒</div>
            <h3>Parent Permission Required</h3>
            <p>{error.message}</p>
            <p className="access-denied-hint">Please ask your parent to log in to approve friend requests.</p>
          </div>
        ) : loading ? (
          <div className="parent-loading">
            <div className="spinner"></div>
            <p>Loading invites...</p>
          </div>
        ) : error && error.type === 'generic' ? (
          <div className="parent-error">
            <div className="error-icon">⚠️</div>
            <h3>Oops!</h3>
            <p>{error.message}</p>
            <button className="btn btn-primary" onClick={loadInvites}>Try Again</button>
          </div>
        ) : invites.length > 0 ? (
          <div className="invites-grid">
            {invites.map((invite) => (
              <motion.div
                key={invite.invite_id}
                className="invite-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="invite-card-header">
                  <h3>{invite.invited_name}</h3>
                  <span className="invite-age">Age {invite.invited_age}</span>
                </div>

                <div className="invite-details">
                  <div className="invite-detail">
                    <strong>Invited by:</strong> {invite.invited_by_name}
                  </div>
                  <div className="invite-detail">
                    <strong>Invite Code:</strong> {invite.invite_code}
                  </div>
                  <div className="invite-detail">
                    <strong>Created:</strong>{' '}
                    {new Date(invite.created_at).toLocaleDateString()}
                  </div>
                </div>

                <div className="invite-actions">
                  <motion.button
                    className="btn btn-success"
                    onClick={() => handleApprove(invite.invite_id)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    ✅ Approve
                  </motion.button>
                  <motion.button
                    className="btn btn-secondary"
                    onClick={() => handleReject(invite.invite_id)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    ❌ Deny
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="parent-empty">
            <div className="empty-icon">✅</div>
            <h3>No pending invites</h3>
            <p>All friend requests have been processed!</p>
          </div>
        )}
      </div>
    </motion.div>
  )
}
